<template>
  <b-row>
    <b-col>
      <b-card title="Filters">
        <b-form>
          <b-row>
            <b-col
              lg="7"
              mdv="7"
            >
              <b-form-group
                id="groupBy"
                label="Group by"
                label-for="grouping"
              >
                <v-select
                  v-model="filters.grouping"
                  multiple
                  :options="groupingOptionsList"
                  input-id="grouping"
                />
              </b-form-group>
            </b-col>
            <b-col
              lg="3"
              mdv="3"
            >
              <b-form-group
                label="Date range"
                label-for="date-range"
              >
                <div class="d-flex align-items-center">
                  <flat-pickr
                    id="date-range"
                    v-model="filters.dateRange"
                    class="form-control"
                    :config="datepickerConfig"
                  />
                  <feather-icon
                    v-b-tooltip.hover.right="tooltipMessage"
                    icon="AlertCircleIcon"
                    class="ml-1"
                    size="1.5x"
                  />
                </div>
              </b-form-group>

            </b-col>
            <b-col>
              <div
                class="d-flex align-items-center"
                style="margin-top: 20px"
              >
                <b-button
                  type="button"
                  variant="primary"
                  class="mr-1"
                  @click="fetchData"
                >
                  Filter
                </b-button>
                <b-button
                  type="button"
                  variant="outline-primary"
                  :disabled="excelLoading"
                  @click="sendExcel"
                >
                  <b-spinner
                    v-if="excelLoading"
                    small
                    class="mr-50"
                  />
                  Download as Excel
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </b-card>

      <b-card>
        <b-card-actions
          ref="loadingCard"
          no-actions
        >
          <b-table
            :items="tableDataStore"
            responsive
            :fields="visibleFields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="isSortDirDesc"
            :busy="loading"
            class="mb-0"
            show-empty
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle mr-1" />
              </div>
            </template>
            <template v-slot:bottom-row>
              <b-td class="left">
                <strong>Total</strong>
              </b-td>
              <b-td
                v-for="field in totals.emptyFields"
                :key="field"
                class="text-right text-nowrap"
              >
                <strong />
              </b-td>
              <b-td class="text-right text-nowrap">
                <strong>{{ totals.goods_shipments | formatNumber }}</strong>
              </b-td>
              <b-td class="text-right text-nowrap">
                <strong>{{ totals.goods_items | formatNumber }}</strong>
              </b-td>
              <b-td class="text-right text-nowrap">
                <strong>{{ totals.goods_items_weight | formatNumber(3) }}</strong>
              </b-td>
              <b-td class="text-right text-nowrap">
                <strong>{{ totals.arrived_goods_shipments | formatNumber }}</strong>
              </b-td>
              <b-td class="text-right text-nowrap">
                <strong>{{ totals.arrived_goods_items | formatNumber }}</strong>
              </b-td>
              <b-td class="text-right text-nowrap">
                <strong>{{ totals.arrived_goods_items_weight | formatNumber(3) }}</strong>
              </b-td>
              <b-td class="text-right text-nowrap">
                <strong>{{ totals.submitted_declarations | formatNumber }}</strong>
              </b-td>
              <b-td class="text-right text-nowrap">
                <strong>{{ totals.submitted_declarations_weight | formatNumber(3) }}</strong>
              </b-td>
              <b-td class="text-right text-nowrap">
                <strong>{{ totals.cleared_declarations | formatNumber }}</strong>
              </b-td>
              <b-td
                v-if="totals.submitted_declarations_h1"
                class="text-right text-nowrap"
              >
                <strong>{{ totals.submitted_declarations_h1 | formatNumber }}</strong>
              </b-td>
              <b-td
                v-if="totals.submitted_declarations_weight_h1"
                class="text-right text-nowrap"
              >
                <strong>{{ totals.submitted_declarations_weight_h1 | formatNumber }}</strong>
              </b-td>
              <b-td
                v-if="totals.cleared_declarations_h1"
                class="text-right text-nowrap"
              >
                <strong>{{ totals.cleared_declarations_h1 | formatNumber }}</strong>
              </b-td>
              <b-td
                v-if="totals.exported_goods_shipments"
                class="text-right text-nowrap"
              >
                <strong>{{ totals.exported_goods_shipments | formatNumber }}</strong>
              </b-td>
              <b-td
                v-if="totals.ens_declarations_goods_shipments"
                class="text-right text-nowrap"
              >
                <strong>{{ totals.ens_declarations_goods_shipments | formatNumber }}</strong>
              </b-td>
              <b-td
                v-if="totals.ens_declarations_items"
                class="text-right text-nowrap"
              >
                <strong>{{ totals.ens_declarations_items | formatNumber }}</strong>
              </b-td>
              <b-td
                v-if="totals.ens_declarations_weight"
                class="text-right text-nowrap"
              >
                <strong>{{ totals.ens_declarations_weight | formatNumber }}</strong>
              </b-td>
              <b-td class="text-right text-nowrap">
                <strong>{{ totals.customs_duty_total | formatNumber(2) }}</strong>
              </b-td>
            </template>
          </b-table>
        </b-card-actions>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  BCard,
  BRow,
  BButton,
  BCol,
  BTable,
  BFormGroup,
  BForm,
  BTd,
  BSpinner, VBTooltip,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import moment from 'moment'
import vSelect from 'vue-select'
import Vue from 'vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { getSelectedCompanies, searchQuery } from '@core/utils/filter'
import numberFormat from '@/libs/number-format'
import { EventBus } from '@core/utils/utils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import handleError from '@/views/components/errorHandler'

export default {
  components: {
    flatPickr,
    BCard,
    BCardActions,
    BRow,
    BCol,
    BTable,
    BFormGroup,
    BForm,
    BButton,
    vSelect,
    BTd,
    BSpinner,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      loading: false,
      fields: [
        {
          key: 'goods_shipments',
          label: 'Created parcels',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
        },
        {
          key: 'goods_items',
          label: 'Created Items',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
        },
        {
          key: 'goods_items_weight',
          label: 'Created Weight',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
        },
        {
          key: 'arrived_goods_shipments',
          label: 'Arrived parcels',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
        },
        {
          key: 'arrived_goods_items',
          label: 'Arrived Items',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
        },
        {
          key: 'arrived_goods_items_weight',
          label: 'Arrived Weight',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
        },
        {
          key: 'submitted_declarations',
          label: 'Submitted H7',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
        },
        {
          key: 'submitted_declarations_weight',
          label: 'Submitted Weight H7',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
        },
        {
          key: 'cleared_declarations',
          label: 'Cleared H7',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
        },
        {
          key: 'submitted_declarations_h1',
          label: 'Submitted H1',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
        },
        {
          key: 'submitted_declarations_weight_h1',
          label: 'Submitted Weight H1',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
        },
        {
          key: 'cleared_declarations_h1',
          label: 'Cleared H1',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
        },
        {
          key: 'exported_goods_shipments',
          label: 'Exported parcels',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
          // hide when no data to show
        },
        {
          key: 'ens_declarations_goods_shipments',
          label: 'ENS parcels',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
          // hide when no data to show
        },
        {
          key: 'ens_declarations_items',
          label: 'ENS items',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
          // hide when no data to show
        },
        {
          key: 'ens_declarations_weight',
          label: 'ENS weight',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: this.formatNumber,
          // hide when no data to show
        },
        {
          key: 'customs_duty_total',
          label: 'Customs duty (EUR)',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right text-nowrap',
          formatter: value => this.formatNumber(value, 'customs_duty_total'),
        },
      ],
      tableDataStore: [],
      sortBy: 'grouping',
      isSortDirDesc: true,
      excelLoading: false,
      totals: {
        emptyFields: [],
      },
      filters: {
        grouping: [{ value: 'shipment', label: 'Shipment' }],
        dateRange: '',
      },
      datepickerConfig: {
        mode: 'range',
        maxDate: moment().format('YYYY-MM-DD'),
      },
      latestReportDate: '',
      companyIds: getSelectedCompanies([this.$activeCompany().data.company.id]),
      groupingOptions: [
        { label: 'Shipment', value: 'shipment' },
        { label: 'Day of the year', value: 'day' },
        { label: 'Week of the month', value: 'week' },
        { label: 'Month of the year', value: 'month' },
      ],
    }
  },
  computed: {
    tooltipMessage() {
      return this.latestReportDate
        ? `The calendar shows only days that have volumes within the selected period. The most recent volumes were on ${this.latestReportDate}.`
        : 'Reports are generated once a day to optimize x7trade performance. No reports have been generated so far'
    },
    visibleFields() {
      return this.fields.filter(field => {
        const conditionalFields = [
          'exported_goods_shipments',
          'ens_declarations_goods_shipments',
          'ens_declarations_items',
          'ens_declarations_weight',
          'submitted_declarations_h1',
          'submitted_declarations_weight_h1',
          'cleared_declarations_h1',
        ]
        return !conditionalFields.includes(field.key) || this.totals[field.key] > 0
      })
    },
    userCompaniesCount() {
      return Vue.prototype.$profile().data.user.companies.length
    },
    groupingOptionsList() {
      const list = [...this.groupingOptions]

      if (this.userCompaniesCount > 1) {
        list.push({ label: 'Company', value: 'company' })
      }

      return list
    },
  },
  watch: {
    sortBy() {
      this.fetchData()
    },
    isSortDirDesc() {
      this.fetchData()
    },
  },
  created() {
    EventBus.$on('companyListUpdated', this.fetchData)
    this.fetchData()
  },
  beforeDestroy() {
    EventBus.$off('companyListUpdated', this.fetchData)
  },
  methods: {
    calculateGroupingColumns() {
      // Remove grouping columns
      this.fields = this.fields.filter(item => !item.groupingColumn)

      // Create a new column for each selected grouping
      const columns = []
      this.totals.emptyFields = []
      this.filters.grouping.forEach((item, idx) => {
        columns.push({
          key: `grouping[${idx}]`,
          label: item.label,
          groupingColumn: true,
          sortable: true,
          thClass: 'text-left',
          tdClass: 'text-left text-nowrap',
        })
        // Skip first, one column is predefined (Total)
        if (idx === 0) return
        this.totals.emptyFields.push({
          key: `grouping[${idx}]`,
          value: item.label,
        })
      })

      // Add grouping columns to the beginning of the table
      this.fields = columns.concat(this.fields)
    },
    fetchData() {
      this.resetData()
      this.loading = true
      this.$http.get(searchQuery(`/v1/companies/${this.$activeCompany().data.company.id}/report/parcels`, {
        ...this.filters,
        sortBy: this.sortBy,
        sortDesc: this.isSortDirDesc ? 'true' : 'false',
      }))
        .then(response => {
          this.tableDataStore = response.data.data
          this.totals = response.data.meta.totals
          this.latestReportDate = response.data.meta.latest_report_date
          if (this.latestReportDate) {
            this.filters.dateRange = `${response.data.meta.filters.dateFrom} to ${response.data.meta.filters.dateTo}`
            this.datepickerConfig.maxDate = this.latestReportDate
          }
          this.calculateGroupingColumns()
        })
        .catch(() => {
          this.calculateGroupingColumns()
        })
        .finally(() => {
          this.loading = false
        })
    },
    resetData() {
      this.tableDataStore = []
      this.totals = { emptyFields: [] }
      this.latestReportDate = ''
    },
    formatNumber(value, key) {
      const weightFields = [
        'goods_items_weight',
        'arrived_goods_items_weight',
        'submitted_declarations_weight',
        'submitted_declarations_weight_h1',
        'ens_declarations_weight',
      ]
      let decimals = 0
      if (weightFields.includes(key)) decimals = 3
      if (key === 'customs_duty_total') decimals = 2
      return numberFormat.format(value, decimals)
    },
    sendExcel() {
      this.excelLoading = true
      this.$http.get(searchQuery('/v1/report/goods-shipments/excel', {
        ...this.filters,
        sortBy: this.sortBy,
        sortDesc: this.isSortDirDesc ? 'true' : 'false',
      }))
        .then(response => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: response.data.message,
              icon: 'MailIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';
</style>
